import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: "▹";
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      background: transparent;
      outline: 0;

      &:after {
        top: 15px;
        left: 15px;
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    // .img {
    //   position: relative;
    //   border-radius: var(--border-radius);
    //   mix-blend-mode: multiply;
    //   filter: grayscale(100%) contrast(1);
    //   transition: var(--transition);
    // }

    &:before,
    &:after {
      content: "";
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 20px;
      left: 20px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          {/*
            Suggested research-forward alt copy for a graduate-school audience.
            Keeps education + specialization up front, then leads with the
            thesis research (now also its own Research section). Edit freely:

            <p>
              I'm a Senior Software Engineer at Goldman Sachs and a computer
              scientist trained in AI & Machine Learning. I graduated as the
              Valedictorian of NTU's School of Computer Science & Engineering,
              with a minor in Mathematics and Honours (Distinction).
            </p>
            <p>
              My research sits at the boundary of neuroscience and deep
              learning. For my thesis at NTU's Centre for Brain and Computing
              Research, I built a deep-learning emotion-profiling system from
              64-channel EEG toward a neurofeedback-driven music therapy
              framework — combining my technical work with a long-standing
              commitment to mental health.
            </p>
          */}
          <div>
            <p>
              I currently work as a full-time software engineer at Goldman
              Sachs. I graduated as the{' '}
              <a href="https://www.ntu.edu.sg/scse/news-events/news/detail/convocation-2023-valedictorian">
                Valedictorian
              </a>{' '}
              from the School of Computer Science and Engineering at NTU,
              earning a Bachelor's degree in Computer Science.
            </p>
            <p>
              My enthusiasm for technology is a driving force in my career.
              Writing my first lines of code at 10 unlocked endless
              possibilities for me, and over the years I have witnessed my high
              school fascination with code evolve into a professional craft. I
              view engineering as a continuous pursuit of solving complex
              problems through elegant, scalable logic.
            </p>

            <p>
              I bring focus, competitiveness, inquisitiveness, and an analytical
              mindset to the table. I thrive in a competitive environment which
              pushes me to elevate both my own performance and that of my team.
              I am inquisitive and like collecting information – words, facts,
              books, and quotations. I find the world exciting precisely because
              of its infinite variety and complexity. Finding answers to my
              questions drives me to uncover the essential facts needed for
              excellence in any endeavor.
            </p>
          </div>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
